/**
 * Verification Page
 * Displays masters pending verification with approve/reject actions
 * Requirements: 16.1
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  UserCheck, 
  RefreshCw, 
  AlertCircle,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { useMasters, useApproveMaster, useRejectMaster } from '@/hooks/useMasters';
import { MasterCard } from '@/components/verification/MasterCard';
import { useToast } from '@/components/ui/toaster';

type TabValue = 'pending' | 'approved' | 'rejected';

interface TabConfig {
  value: TabValue;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  status?: 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED';
  onlyMyApproved?: boolean;
}

const TABS: TabConfig[] = [
  { value: 'pending', label: 'Ожидают', icon: Clock, status: 'PENDING_REVIEW' },
  { value: 'approved', label: 'Мои одобрения', icon: CheckCircle2, onlyMyApproved: true },
  { value: 'rejected', label: 'Отклонены', icon: XCircle, status: 'REJECTED' },
];

export function VerificationPage() {
  const [activeTab, setActiveTab] = useState<TabValue>('pending');
  const { toast } = useToast();

  // Get current tab config
  const currentTab = TABS.find(t => t.value === activeTab) || TABS[0];

  // Fetch masters with current filter
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useMasters({
    status: currentTab.status,
    onlyMyApproved: currentTab.onlyMyApproved,
    limit: 50,
  });

  // Mutations
  const approveMutation = useApproveMaster();
  const rejectMutation = useRejectMaster();

  // Handlers
  const handleApprove = async (masterId: string) => {
    try {
      await approveMutation.mutateAsync(masterId);
      toast({
        title: 'Мастер одобрен',
        description: 'Статус мастера успешно обновлён',
      });
      // Provide haptic feedback
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      toast({
        title: 'Ошибка',
        description: err instanceof Error ? err.message : 'Не удалось одобрить мастера',
        variant: 'destructive',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
    }
  };

  const handleReject = async (masterId: string, reason: string) => {
    try {
      await rejectMutation.mutateAsync({ masterId, reason });
      toast({
        title: 'Мастер отклонён',
        description: 'Заявка мастера отклонена',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      toast({
        title: 'Ошибка',
        description: err instanceof Error ? err.message : 'Не удалось отклонить мастера',
        variant: 'destructive',
      });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
    }
  };

  const masters = data?.data || [];
  const total = data?.pagination?.total || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Верификация мастеров</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.value;
          return (
            <Button
              key={tab.value}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              className="shrink-0"
              onClick={() => setActiveTab(tab.value)}
            >
              <Icon className="h-4 w-4 mr-1" />
              {tab.label}
              {tab.value === 'pending' && total > 0 && isActive && (
                <Badge variant="secondary" className="ml-2">
                  {total}
                </Badge>
              )}
            </Button>
          );
        })}
      </div>

      {/* Content */}
      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-2" />
            <p className="text-destructive font-medium">Ошибка загрузки</p>
            <p className="text-sm text-muted-foreground mb-4">
              {error?.message || 'Не удалось загрузить данные'}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Повторить
            </Button>
          </CardContent>
        </Card>
      ) : masters.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-muted-foreground" />
              {currentTab.label}
            </CardTitle>
            <CardDescription>
              {activeTab === 'pending' && 'Мастера, ожидающие проверки документов'}
              {activeTab === 'approved' && 'Мастера, которых вы одобрили'}
              {activeTab === 'rejected' && 'Отклонённые заявки'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <UserCheck className="mb-2 h-12 w-12 opacity-50" />
              <p>
                {activeTab === 'pending' && 'Нет мастеров на верификации'}
                {activeTab === 'approved' && 'Вы ещё не одобрили ни одного мастера'}
                {activeTab === 'rejected' && 'Нет отклонённых заявок'}
              </p>
              <p className="text-sm">
                {activeTab === 'pending' && 'Новые заявки появятся здесь'}
                {activeTab === 'approved' && 'Здесь будут мастера, которых вы одобрили'}
                {activeTab === 'rejected' && 'Отклонённые заявки появятся здесь'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Stats */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Всего: {total}</span>
            {isFetching && (
              <span className="flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Обновление...
              </span>
            )}
          </div>

          {/* Master Cards */}
          {masters.map((master) => (
            <MasterCard
              key={master.id}
              master={master}
              onApprove={handleApprove}
              onReject={handleReject}
              isApproving={
                approveMutation.isPending && 
                approveMutation.variables === master.id
              }
              isRejecting={
                rejectMutation.isPending && 
                rejectMutation.variables?.masterId === master.id
              }
            />
          ))}

          {/* Pagination info */}
          {data?.pagination && data.pagination.totalPages > 1 && (
            <div className="text-center text-sm text-muted-foreground">
              Страница {data.pagination.page} из {data.pagination.totalPages}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
